/**
 * Technician job screen: "On my way" (tracker item 43). Emails the customer
 * that the technician is on the way, once per job (a repeat tap returns the
 * first message). Shown only while the company has the message turned on
 * and the job is booked or under way; afterwards it says when the customer
 * was notified, or why the message could not go out (e.g. no email).
 * Best effort: if the state cannot be loaded the button is simply not shown.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { INotificationsApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';

type OnMyWayApi = Partial<Pick<INotificationsApi, 'getJobCustomerNotifications' | 'sendJobCustomerNotification'>>;

interface Props {
  api: OnMyWayApi;
  jobId: string;
  /** Reloads when the job's status changes */
  jobStatus: string;
  /** Disabled while another action on the screen is running */
  disabled?: boolean;
}

/** Same as the Start / Stop / Finish buttons on the technician job screen */
const bigButton = 'flex w-full min-h-[56px] items-center justify-center rounded-lg px-4 text-base font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed';

export function TechnicianOnMyWayButton({ api, jobId, jobStatus, disabled }: Props) {
  const [event, setEvent] = useState<types.DayOfEventState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!api.getJobCustomerNotifications) return;
    try {
      const state = await api.getJobCustomerNotifications(jobId);
      setEvent(state.events.find((e) => e.eventKey === 'JOB_ON_MY_WAY') ?? null);
    } catch {
      setEvent(null);
    }
  }, [api, jobId]);

  useEffect(() => { void load(); }, [load, jobStatus]);

  if (!event || !event.enabled) return null;
  const last = event.lastMessage;

  if (last) {
    const when = new Date(last.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return (
      <p className="text-center text-sm text-gray-700" role="status" data-testid="on-my-way-status">
        {last.status === 'SKIPPED'
          ? `The customer was not notified: ${last.skipReasonText ?? 'the message could not be sent'}.`
          : `Customer told you are on the way (${when}).`}
      </p>
    );
  }
  if (!event.canSend) return null;

  async function send() {
    if (busy || !api.sendJobCustomerNotification) return;
    setBusy(true);
    setError(null);
    try {
      await api.sendJobCustomerNotification(jobId, 'JOB_ON_MY_WAY');
      await load();
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not notify the customer.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button type="button" className={bigButton + ' bg-indigo-600 hover:bg-indigo-700'} disabled={busy || disabled} onClick={() => void send()}>
        On my way
      </button>
      {event.wouldSkipText && <p className="mt-1 text-center text-xs text-amber-800">The customer will not get it: {event.wouldSkipText}.</p>}
      {error && <p className="mt-1 text-center text-sm text-red-700" role="alert">{error}</p>}
    </div>
  );
}
